import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useFarm } from '../context/FarmContext';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { getTaskContextDisplay, getTaskDisplayTitle } from '../utils/taskDisplay';
import ImageUploadInput from '../components/ImageUploadInput';
import { isWorkerRole, normalizeRole } from '../utils/roles';
import {
  validateImageFiles,
  getImageErrorMessage,
} from '../utils/imageUpload';
import '../styles/admin-pages.css';
import './TasksPage.css';

const TASK_TYPE_KEYS = [
  'climate',
  'watering',
  'plant_inspection',
  'equipment',
  'cuttings',
  'nutrition',
  'pests_disease',
  'cleaning',
  'other',
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function hasValidDueAt(task) {
  if (!task?.dueAt) return false;
  const t0 = new Date(task.dueAt).getTime();
  return !Number.isNaN(t0);
}

function endOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Same calendar day, at least ~2h ahead, capped at end of local day */
function computeDueLaterToday() {
  const now = new Date();
  const candidate = new Date(now);
  candidate.setHours(candidate.getHours() + 2);
  const end = endOfLocalDay(now);
  return candidate.getTime() > end.getTime() ? end : candidate;
}

function computeDueTomorrowMorning() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

function computeDueTomorrowEvening() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(18, 0, 0, 0);
  return d;
}

function toDatetimeLocalValue(d) {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function priorityToTier(p) {
  const n = Number.isFinite(p) ? p : 3;
  if (n >= 5) return 'urgent';
  if (n >= 4) return 'high';
  if (n >= 2) return 'medium';
  return 'low';
}

function tierToPriority(tier) {
  switch (tier) {
    case 'low':
      return 1;
    case 'medium':
      return 3;
    case 'high':
      return 4;
    case 'urgent':
      return 5;
    default:
      return 3;
  }
}

function isLikelyObjectId(value) {
  return typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value.trim());
}

/** Resolved entity labels joined; if none, free-text location. */
function buildTaskContextDisplay(task, maps, t) {
  if (!task) return '';
  const resolved = getTaskContextDisplay(task, maps);
  if (resolved) return resolved;
  const parts = [];
  const rid = task.roomId != null ? String(task.roomId) : '';
  const did = task.deviceId != null ? String(task.deviceId) : '';
  const sid = task.strainId != null ? String(task.strainId) : '';
  const cid = task.cuttingId != null ? String(task.cuttingId) : '';

  if (rid) {
    const name = maps.rooms.get(rid);
    if (name) parts.push(name);
  }
  if (did) {
    const name = maps.devices.get(did);
    if (name) parts.push(name);
  }
  if (sid) {
    const name = maps.strains.get(sid);
    if (name) parts.push(name);
  }
  if (cid) {
    const label = maps.cuttings.get(cid);
    if (label) parts.push(label);
    else parts.push(`${t('tasksDashboard.cuttingLabel')} · ${cid.slice(-6)}`);
  }

  if (parts.length > 0) return parts.join(' · ');
  return (task.location || '').trim();
}

const UNASSIGNED_ROOM_GROUP_KEY = '__unassigned__';

function getResolvedRoomGroupKey(task, maps) {
  const rid = task.roomId != null ? String(task.roomId) : '';
  if (rid && maps.rooms.has(rid)) return rid;
  return UNASSIGNED_ROOM_GROUP_KEY;
}

function sortTasksInRoomGroup(a, b) {
  const pa = a.priority ?? 3;
  const pb = b.priority ?? 3;
  if (pb !== pa) return pb - pa;
  const da = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY;
  const db = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY;
  if (da !== db) return da - db;
  const ca = new Date(a.createdAt || 0).getTime();
  const cb = new Date(b.createdAt || 0).getTime();
  return cb - ca;
}

function computeRoomGroupRank(tasks, isOverdueFn, isDueTodayFn) {
  let rank = 2;
  for (const task of tasks) {
    if ((task.priority ?? 3) >= 5 || isOverdueFn(task)) {
      return 0;
    }
  }
  for (const task of tasks) {
    if (isDueTodayFn(task)) {
      rank = 1;
      break;
    }
  }
  return rank;
}

function buildTaskSearchHaystack(task, maps) {
  const parts = [task.description || '', task.location || ''];
  const rid = task.roomId != null ? String(task.roomId) : '';
  if (rid && maps.rooms.has(rid)) parts.push(maps.rooms.get(rid));
  const did = task.deviceId != null ? String(task.deviceId) : '';
  if (did && maps.devices.has(did)) parts.push(maps.devices.get(did));
  const sid = task.strainId != null ? String(task.strainId) : '';
  if (sid && maps.strains.has(sid)) parts.push(maps.strains.get(sid));
  const cid = task.cuttingId != null ? String(task.cuttingId) : '';
  if (cid) {
    parts.push(maps.cuttings.get(cid) || '');
    parts.push(cid);
  }
  if (Array.isArray(task.comments)) {
    task.comments.forEach((c) => parts.push(c.text || ''));
  }
  parts.push(
    task.reason || '',
    task.triggerType || '',
    task.triggerMetric || '',
    task.triggerUnit || ''
  );
  if (task.triggerValue != null && task.triggerValue !== '') {
    parts.push(String(task.triggerValue));
  }
  if (task.thresholdMin != null && task.thresholdMin !== '') parts.push(String(task.thresholdMin));
  if (task.thresholdMax != null && task.thresholdMax !== '') parts.push(String(task.thresholdMax));
  return parts.join(' ');
}

function getTaskCardNoteParts(task) {
  const sys = (task.source || 'manual') === 'system';
  const structured = sys && task.reason && String(task.reason).trim();
  if (structured) {
    return { text: structured, labelPath: 'tasksDashboard.systemReasonPrimaryLabel' };
  }
  const first = Array.isArray(task.comments) && task.comments[0]?.text;
  if (first) {
    return { text: String(first).trim(), labelPath: 'tasksDashboard.reason' };
  }
  return null;
}

function hasSystemTriggerData(task) {
  if ((task.source || 'manual') !== 'system') return false;
  const metric = task.triggerMetric != null && String(task.triggerMetric).trim();
  const ttype = task.triggerType != null && String(task.triggerType).trim();
  const unit = task.triggerUnit != null && String(task.triggerUnit).trim();
  const val = task.triggerValue;
  const hasVal = val != null && val !== '' && Number.isFinite(Number(val));
  const min = task.thresholdMin;
  const max = task.thresholdMax;
  const hasMin = min != null && min !== '' && Number.isFinite(Number(min));
  const hasMax = max != null && max !== '' && Number.isFinite(Number(max));
  const hasTime =
    task.triggeredAt && !Number.isNaN(new Date(task.triggeredAt).getTime());
  return !!(metric || ttype || unit || hasVal || hasMin || hasMax || hasTime);
}

function formatSystemTriggerSummary(task, t, formatDateFn) {
  const parts = [];
  const metric = task.triggerMetric != null ? String(task.triggerMetric).trim() : '';
  const ttype = task.triggerType != null ? String(task.triggerType).trim() : '';
  if (metric) parts.push(metric);
  else if (ttype) parts.push(ttype);
  const val = task.triggerValue;
  const unit = task.triggerUnit != null ? String(task.triggerUnit).trim() : '';
  if (val != null && val !== '' && Number.isFinite(Number(val))) {
    const n = Number(val);
    parts.push(unit ? `${n}\u00a0${unit}` : String(n));
  }
  const nMin =
    task.thresholdMin != null && task.thresholdMin !== '' && Number.isFinite(Number(task.thresholdMin))
      ? Number(task.thresholdMin)
      : null;
  const nMax =
    task.thresholdMax != null && task.thresholdMax !== '' && Number.isFinite(Number(task.thresholdMax))
      ? Number(task.thresholdMax)
      : null;
  if (nMin != null && nMax != null) {
    parts.push(t('tasksDashboard.systemTriggerRange', nMin, nMax));
  } else if (nMin != null) {
    parts.push(t('tasksDashboard.systemTriggerMinOnly', nMin));
  } else if (nMax != null) {
    parts.push(t('tasksDashboard.systemTriggerMaxOnly', nMax));
  }
  if (task.triggeredAt) {
    const d = new Date(task.triggeredAt);
    if (!Number.isNaN(d.getTime())) {
      parts.push(`${t('tasksDashboard.systemTriggeredAt')} ${formatDateFn(d)}`);
    }
  }
  return parts.length ? parts.join(' · ') : '';
}

function formatSystemValueWithUnit(task) {
  const val = task.triggerValue;
  if (val == null || val === '' || !Number.isFinite(Number(val))) return '';
  const unit = task.triggerUnit != null ? String(task.triggerUnit).trim() : '';
  const n = Number(val);
  return unit ? `${n}\u00a0${unit}` : String(n);
}

function formatSystemAllowedRangeText(task, t) {
  const nMin =
    task.thresholdMin != null && task.thresholdMin !== '' && Number.isFinite(Number(task.thresholdMin))
      ? Number(task.thresholdMin)
      : null;
  const nMax =
    task.thresholdMax != null && task.thresholdMax !== '' && Number.isFinite(Number(task.thresholdMax))
      ? Number(task.thresholdMax)
      : null;
  if (nMin != null && nMax != null) return t('tasksDashboard.systemTriggerRange', nMin, nMax);
  if (nMin != null) return t('tasksDashboard.systemTriggerMinOnly', nMin);
  if (nMax != null) return t('tasksDashboard.systemTriggerMaxOnly', nMax);
  return '';
}

function TaskCardNotes({ task, t, formatDate }) {
  const sys = (task.source || 'manual') === 'system';
  const note = getTaskCardNoteParts(task);
  const triggerSummary =
    sys && hasSystemTriggerData(task) ? formatSystemTriggerSummary(task, t, formatDate) : '';
  if (!note && !triggerSummary) return null;
  return (
    <>
      {note ? (
        <p className="tasks-task-card__reason">
          <strong className="me-1">{t(note.labelPath)}:</strong>
          {note.text}
        </p>
      ) : null}
      {triggerSummary ? (
        <p className="tasks-task-card__trigger small text-muted">{triggerSummary}</p>
      ) : null}
    </>
  );
}

function taskMatchesDashboardFilters(task, maps, f) {
  if (f.room) {
    const key = getResolvedRoomGroupKey(task, maps);
    if (key !== f.room) return false;
  }
  if (f.taskType && (task.taskType || 'other') !== f.taskType) return false;
  if (f.priority && priorityToTier(task.priority) !== f.priority) return false;
  if (f.source && (task.source || 'manual') !== f.source) return false;
  if (f.search && f.search.trim()) {
    const q = f.search.trim().toLowerCase();
    if (!buildTaskSearchHaystack(task, maps).toLowerCase().includes(q)) return false;
  }
  return true;
}

export default function TasksPage() {
  const { t, lang } = useLang();
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const [list, setList] = useState([]);
  const [mainTab, setMainTab] = useState('today');
  const [showCompletedMixed, setShowCompletedMixed] = useState(false);
  const [filterRoom, setFilterRoom] = useState('');
  const [filterTaskType, setFilterTaskType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const [desc, setDesc] = useState('');
  const [comment, setComment] = useState('');
  const [location, setLocation] = useState('');
  const [taskType, setTaskType] = useState('other');
  const [priorityTier, setPriorityTier] = useState('medium');
  const [dueLocal, setDueLocal] = useState('');
  const [files, setFiles] = useState([]);
  const [entityRooms, setEntityRooms] = useState([]);
  const [entityDevices, setEntityDevices] = useState([]);
  const [entityStrains, setEntityStrains] = useState([]);
  const [entityCuttings, setEntityCuttings] = useState([]);
  const [refRoomId, setRefRoomId] = useState('');
  const [refDeviceId, setRefDeviceId] = useState('');
  const [refStrainId, setRefStrainId] = useState('');
  const [refCuttingId, setRefCuttingId] = useState('');

  const { effectiveFarmId } = useFarm();

  const [editing, setEditing] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [newComments, setNewComments] = useState({});
  const [viewImg, setViewImg] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [postponeTarget, setPostponeTarget] = useState(null);
  const [postponeCustomLocal, setPostponeCustomLocal] = useState('');
  const [prioritySaving, setPrioritySaving] = useState({});
  const [completingTaskIds, setCompletingTaskIds] = useState({});
  const fileInputRef = useRef(null);
  const { token: authToken, user } = useAuth();
  const token = authToken || localStorage.getItem('token');

  const decode = (tok) => {
    if (!tok) return {};
    try {
      const d = JSON.parse(atob(tok.split('.')[1]));
      return {
        role: d.role,
        id: d.id,
        login: d.login,
        email: d.email,
      };
    } catch {
      return {};
    }
  };
  const decoded = decode(token);
  const role = user?.role || decoded.role;
  const normalizedRole = normalizeRole(role);
  const isStrainsAdmin = ['admin', 'superuser'].includes(normalizedRole);
  const taskFarmContextId = effectiveFarmId ? String(effectiveFarmId) : undefined;
  const authIdentityIds = new Set(
    [decoded.id, user?.id].map((value) => String(value || '').trim()).filter(Boolean)
  );
  const authIdentityLogins = new Set(
    [decoded.login, decoded.email, user?.email, localStorage.getItem('login')]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const taskRequest = useCallback(
    (url, options = {}) => authFetch(url, options, { token, farmContextId: taskFarmContextId }),
    [token, taskFarmContextId]
  );

  const formatDate = useCallback(
    (d) => {
      const date = new Date(d);
      const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang === 'ua' ? 'uk-UA' : lang;
      return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    [lang]
  );

  const priorityTierLabel = useCallback(
    (tier) => {
      switch (tier) {
        case 'low':
          return t('tasksDashboard.priorityLow');
        case 'medium':
          return t('tasksDashboard.priorityMedium');
        case 'high':
          return t('tasksDashboard.priorityHigh');
        case 'urgent':
          return t('tasksDashboard.priorityUrgent');
        default:
          return t('tasksDashboard.priorityMedium');
      }
    },
    [t]
  );

  const taskTypeLabel = useCallback(
    (key) => t(`tasksDashboard.taskTypes.${key}`) || key,
    [t]
  );

  useEffect(() => {
    if (!token || !effectiveFarmId) {
      setEntityRooms([]);
      setEntityDevices([]);
      return;
    }
    let cancelled = false;
    const farmId = String(effectiveFarmId);
    authFetch(`/api/rooms?farmId=${encodeURIComponent(farmId)}`, {}, { token, farmContextId: farmId })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (!cancelled) setEntityRooms(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled) setEntityRooms([]);
      });
    authFetch('/api/devices/active', {}, { token, farmContextId: farmId })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (!cancelled) setEntityDevices(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled) setEntityDevices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, effectiveFarmId]);

  useEffect(() => {
    if (!token) {
      setEntityStrains([]);
      setEntityCuttings([]);
      return;
    }
    let cancelled = false;
    const strainUrl = isStrainsAdmin ? '/api/admin/strains' : '/api/strains';
    authFetch(strainUrl, {}, { token })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (!cancelled) setEntityStrains(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled) setEntityStrains([]);
      });
    authFetch('/api/cuttings?skip=0&limit=200', {}, { token })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (!cancelled) setEntityCuttings(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled) setEntityCuttings([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, isStrainsAdmin]);

  const entityMaps = useMemo(() => {
    const rooms = new Map();
    entityRooms.forEach((r) => {
      const id = String(r._id || r.id || '');
      if (id) rooms.set(id, (r.name || '').trim() || id.slice(-6));
    });
    const devices = new Map();
    entityDevices.forEach((d) => {
      const id = String(d._id || d.id || '');
      if (!id) return;
      const label = (d.displayName || d.name || d.deviceId || '').trim() || id.slice(-6);
      devices.set(id, label);
    });
    const strains = new Map();
    entityStrains.forEach((s) => {
      const id = String(s._id || s.id || '');
      if (!id) return;
      strains.set(id, (s.name || s.slug || '').trim() || id.slice(-6));
    });
    const cuttings = new Map();
    entityCuttings.forEach((c) => {
      const id = String(c._id || c.id || '');
      if (!id) return;
      const label = [c.code, c.shortId].filter(Boolean).join(' · ') || id.slice(-6);
      cuttings.set(id, label);
    });
    return { rooms, devices, strains, cuttings };
  }, [entityRooms, entityDevices, entityStrains, entityCuttings]);

  const devicesForSelect = useMemo(() => {
    if (!refRoomId) return entityDevices;
    return entityDevices.filter((d) => String(d.roomId || '') === String(refRoomId));
  }, [entityDevices, refRoomId]);

  const showEntitySelectors =
    entityRooms.length > 0 ||
    entityDevices.length > 0 ||
    entityStrains.length > 0 ||
    entityCuttings.length > 0;

  const fetchTasks = useCallback(() => {
    if (!token) {
      setList([]);
      return;
    }
    taskRequest('/api/tasks')
      .then((res) => res.json())
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch((err) => console.error('Failed to fetch tasks', err));
  }, [taskRequest, token]);

  useEffect(() => {
    if (!token) return;
    fetchTasks();
  }, [token, fetchTasks]);

  const closeDetailModal = () => {
    setEditing(null);
    setDetailTask(null);
  };

  const openDetail = (task) => {
    setEditing(null);
    setDetailTask(task);
  };

  const normalizeStatus = (status) => (status || '').trim().toLowerCase();

  const openList = useMemo(
    () => list.filter((b) => normalizeStatus(b.status) !== 'done'),
    [list]
  );
  const closedList = useMemo(
    () => list.filter((b) => normalizeStatus(b.status) === 'done'),
    [list]
  );

  const sod = startOfDay(Date.now());

  /** Due date calendar day is strictly before today (local) */
  const isOverdue = useCallback(
    (task) => {
      if (!hasValidDueAt(task)) return false;
      return startOfDay(new Date(task.dueAt)) < sod;
    },
    [sod]
  );

  /** Due date falls on today's calendar day (local) */
  const isDueToday = useCallback(
    (task) => {
      if (!hasValidDueAt(task)) return false;
      return startOfDay(new Date(task.dueAt)) === sod;
    },
    [sod]
  );

  /** Due date calendar day is strictly after today (local) */
  const isDueAfterToday = useCallback(
    (task) => {
      if (!hasValidDueAt(task)) return false;
      return startOfDay(new Date(task.dueAt)) > sod;
    },
    [sod]
  );

  const matchesTodayTab = useCallback(
    (task) =>
      !hasValidDueAt(task) || isOverdue(task) || isDueToday(task),
    [isDueToday, isOverdue]
  );

  const summary = useMemo(() => {
    const completed = closedList.length;
    const urgent = openList.filter((b) => (b.priority ?? 3) >= 5).length;
    const today = openList.filter((b) => matchesTodayTab(b)).length;
    const overdue = openList.filter((b) => isOverdue(b)).length;
    const system = openList.filter((b) => (b.source || 'manual') === 'system').length;
    return { urgent, today, overdue, system, completed };
  }, [openList, closedList, matchesTodayTab, isOverdue]);

  const filteredActive = useMemo(() => {
    if (mainTab === 'today') {
      return openList.filter((b) => matchesTodayTab(b));
    }
    if (mainTab === 'problems') {
      return openList.filter((b) => {
        const st = normalizeStatus(b.status);
        return (
          st === 'needs' ||
          st === 'rejected' ||
          (b.priority ?? 3) >= 5 ||
          isOverdue(b)
        );
      });
    }
    if (mainTab === 'scheduled') {
      return openList.filter((b) => isDueAfterToday(b));
    }
    return [];
  }, [mainTab, openList, matchesTodayTab, isDueAfterToday, isOverdue]);

  const dashboardFilterPayload = useMemo(
    () => ({
      room: filterRoom,
      taskType: filterTaskType,
      priority: filterPriority,
      source: filterSource,
      search: filterSearch,
    }),
    [filterRoom, filterTaskType, filterPriority, filterSource, filterSearch]
  );

  const activeTasksAfterFilters = useMemo(() => {
    if (mainTab === 'history') return [];
    return filteredActive.filter((task) =>
      taskMatchesDashboardFilters(task, entityMaps, dashboardFilterPayload)
    );
  }, [mainTab, filteredActive, entityMaps, dashboardFilterPayload]);

  const emptyStateMessage = useMemo(() => {
    const map = {
      today: t('tasksDashboard.emptyToday'),
      problems: t('tasksDashboard.emptyProblems'),
      scheduled: t('tasksDashboard.emptyScheduled'),
      history: t('tasksDashboard.emptyHistory'),
    };
    return map[mainTab] || t('tasksDashboard.noTasksInView');
  }, [mainTab, t]);

  const historyList = useMemo(
    () =>
      closedList
        .slice()
        .sort(
          (a, b) =>
            new Date(b.completedAt || b.lastActivityAt || 0).getTime() -
            new Date(a.completedAt || a.lastActivityAt || 0).getTime()
        ),
    [closedList]
  );

  const historyListFiltered = useMemo(() => {
    if (mainTab !== 'history') return historyList;
    return historyList.filter((task) =>
      taskMatchesDashboardFilters(task, entityMaps, dashboardFilterPayload)
    );
  }, [mainTab, historyList, entityMaps, dashboardFilterPayload]);

  const filtersActive = useMemo(
    () =>
      Boolean(
        filterRoom ||
          filterTaskType ||
          filterPriority ||
          filterSource ||
          (filterSearch && filterSearch.trim())
      ),
    [filterRoom, filterTaskType, filterPriority, filterSource, filterSearch]
  );

  const groupedActiveTasks = useMemo(() => {
    if (mainTab === 'history') return [];
    const byKey = new Map();
    for (const task of activeTasksAfterFilters) {
      const key = getResolvedRoomGroupKey(task, entityMaps);
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(task);
    }
    const groups = [];
    for (const [key, rawTasks] of byKey.entries()) {
      const tasks = rawTasks.slice().sort(sortTasksInRoomGroup);
      const rank = computeRoomGroupRank(tasks, isOverdue, isDueToday);
      const isUnassigned = key === UNASSIGNED_ROOM_GROUP_KEY;
      const roomLabel = isUnassigned
        ? t('tasksDashboard.groupNoRoom')
        : entityMaps.rooms.get(key) || key.slice(-6);
      groups.push({
        key,
        title: roomLabel,
        count: tasks.length,
        tasks,
        rank,
        isUnassigned,
        sortLabel: roomLabel,
      });
    }
    groups.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (a.isUnassigned !== b.isUnassigned) return a.isUnassigned ? 1 : -1;
      return String(a.sortLabel).localeCompare(String(b.sortLabel), undefined, {
        sensitivity: 'base',
      });
    });
    return groups;
  }, [mainTab, activeTasksAfterFilters, entityMaps, isOverdue, isDueToday, t]);

  const activeListEmptyMessage = useMemo(() => {
    if (mainTab === 'history') return emptyStateMessage;
    if (filteredActive.length === 0) return emptyStateMessage;
    if (activeTasksAfterFilters.length === 0 && filtersActive) {
      return t('tasksDashboard.emptyFiltered');
    }
    return emptyStateMessage;
  }, [
    mainTab,
    filteredActive.length,
    activeTasksAfterFilters.length,
    filtersActive,
    emptyStateMessage,
    t,
  ]);

  const historyListEmptyMessage = useMemo(() => {
    if (mainTab !== 'history') return emptyStateMessage;
    if (historyList.length === 0) return emptyStateMessage;
    if (historyListFiltered.length === 0 && filtersActive) {
      return t('tasksDashboard.emptyFiltered');
    }
    return emptyStateMessage;
  }, [
    mainTab,
    historyList.length,
    historyListFiltered.length,
    filtersActive,
    emptyStateMessage,
    t,
  ]);

  const sectionTitle = useMemo(() => {
    const map = {
      today: t('tasksDashboard.sectionToday'),
      problems: t('tasksDashboard.sectionProblems'),
      scheduled: t('tasksDashboard.sectionScheduled'),
      history: t('tasksDashboard.sectionHistory'),
    };
    return map[mainTab] || map.today;
  }, [mainTab, t]);

  const handleFileChange = (e) => {
    const fs = Array.from(e.target.files || []);
    const err = validateImageFiles(fs);
    if (err) {
      alert(getImageErrorMessage(err, t));
      e.target.value = '';
      setFiles([]);
    } else {
      setFiles(fs);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!desc.trim()) return;
    const body = {
      description: desc.trim(),
      comment: comment.trim() || undefined,
      login: localStorage.getItem('login'),
      displayName: localStorage.getItem('login'),
      priority: tierToPriority(priorityTier),
      location: location.trim() || undefined,
      taskType,
      source: 'manual',
    };
    if (dueLocal) {
      const d = new Date(dueLocal);
      if (!Number.isNaN(d.getTime())) body.dueAt = d.toISOString();
    }
    if (isLikelyObjectId(refRoomId)) body.roomId = refRoomId.trim();
    if (isLikelyObjectId(refDeviceId)) body.deviceId = refDeviceId.trim();
    if (isLikelyObjectId(refStrainId)) body.strainId = refStrainId.trim();
    if (isLikelyObjectId(refCuttingId)) body.cuttingId = refCuttingId.trim();
    const res = await taskRequest('/api/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const task = await res.json();
    if (files.length && task._id) {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const upRes = await taskRequest(`/api/tasks/${task._id}/upload`, {
        method: 'POST',
        body: fd,
      });
      const upData = await upRes.json().catch(() => ({}));
      if (!upRes.ok) {
        alert(getImageErrorMessage(upData.error, t));
      }
    }
    setDesc('');
    setComment('');
    setLocation('');
    setRefRoomId('');
    setRefDeviceId('');
    setRefStrainId('');
    setRefCuttingId('');
    setTaskType('other');
    setPriorityTier('medium');
    setDueLocal('');
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    fetchTasks();
  };

  const closeTask = async (id) => {
    if (!id || completingTaskIds[id]) return;
    const completedAt = new Date().toISOString();
    let previousTask = null;
    setCompletingTaskIds((prev) => ({ ...prev, [id]: true }));
    setList((prev) =>
      prev.map((task) => {
        if (task._id !== id) return task;
        previousTask = task;
        return {
          ...task,
          status: 'done',
          completedAt,
          lastActivityAt: completedAt,
        };
      })
    );
    setDetailTask((prev) => {
      if (prev && prev._id === id) {
        setEditing(null);
        return null;
      }
      return prev;
    });
    try {
      const res = await taskRequest(`/api/tasks/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'done' }),
      });
      if (!res.ok) {
        throw new Error('Failed to complete task');
      }
      fetchTasks();
    } catch (err) {
      console.error('Failed to complete task', err);
      if (previousTask) {
        setList((prev) => prev.map((task) => (task._id === id ? previousTask : task)));
      }
    } finally {
      setCompletingTaskIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const closePostponeModal = () => {
    setPostponeTarget(null);
    setPostponeCustomLocal('');
  };

  const openPostponeModal = (task) => {
    setPostponeTarget(task);
    setPostponeCustomLocal(toDatetimeLocalValue(computeDueTomorrowMorning()));
  };

  const applyPostponeDueAt = async (dueDate) => {
    if (!postponeTarget || Number.isNaN(dueDate.getTime())) return;
    const id = postponeTarget._id;
    try {
      const res = await taskRequest(`/api/tasks/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dueAt: dueDate.toISOString() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || t('tasksDashboard.postponeFailed'));
        return;
      }
      const iso = dueDate.toISOString();
      setDetailTask((prev) => (prev && prev._id === id ? { ...prev, dueAt: iso } : prev));
      fetchTasks();
      closePostponeModal();
    } catch (err) {
      console.error('Failed to postpone task', err);
      alert(t('tasksDashboard.postponeFailed'));
    }
  };

  const applyPostponeCustom = () => {
    if (!postponeCustomLocal.trim()) return;
    const d = new Date(postponeCustomLocal);
    if (Number.isNaN(d.getTime())) {
      alert(t('tasksDashboard.postponeInvalidDate'));
      return;
    }
    applyPostponeDueAt(d);
  };

  const saveEdit = async (id) => {
    await taskRequest(`/api/tasks/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ description: editDesc }),
    });
    setEditing(null);
    setEditDesc('');
    fetchTasks();
    setDetailTask((prev) =>
      prev && prev._id === id ? { ...prev, description: editDesc } : prev
    );
  };

  const updateStatus = async (id, status) => {
    await taskRequest(`/api/tasks/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });
    fetchTasks();
  };

  const addComment = async (id) => {
    const text = (newComments[id] || '').trim();
    if (!text) return;
    await taskRequest(`/api/tasks/${id}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });
    setNewComments((prev) => ({ ...prev, [id]: '' }));
    fetchTasks();
  };

  const deleteTask = async (id) => {
    await taskRequest(`/api/tasks/${id}`, {
      method: 'DELETE',
    });
    setList((prev) => prev.filter((x) => x._id !== id));
    setDetailTask((prev) => {
      if (prev && prev._id === id) {
        setEditing(null);
        return null;
      }
      return prev;
    });
  };

  const isTaskCreator = (task) => {
    if (!task) return false;
    const actorIds = [
      task.createdBy?.id,
      task.createdBy?._id,
      task.createdById,
      task.creator?.id,
      task.creator?._id,
      task.creatorId,
      task.userId,
      task.authorId,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    if (actorIds.some((value) => authIdentityIds.has(value))) return true;
    const actorLogins = [
      typeof task.createdBy === 'string' ? task.createdBy : '',
      task.createdBy?.login,
      task.createdBy?.email,
      typeof task.creator === 'string' ? task.creator : '',
      task.creator?.login,
      task.creator?.email,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    return actorLogins.some((value) => authIdentityLogins.has(value));
  };

  const canManagePriority = (task) =>
    isTaskCreator(task) || ['admin', 'superuser'].includes(normalizedRole);

  const canActOnTask = (task) =>
    (isTaskCreator(task) && token) || ['admin', 'superuser'].includes(normalizedRole);

  const changePriority = async (task, delta) => {
    if (!canManagePriority(task)) return;
    const current = Number.isFinite(task.priority) ? task.priority : 3;
    const next = Math.min(5, Math.max(1, current + delta));
    if (next === current) return;
    setPrioritySaving((prev) => ({ ...prev, [task._id]: true }));
    try {
      const res = await taskRequest(`/api/tasks/${task._id}/priority`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ priority: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to update priority');
      }
    } catch (err) {
      console.error('Failed to update priority', err);
    } finally {
      setPrioritySaving((prev) => {
        const nextState = { ...prev };
        delete nextState[task._id];
        return nextState;
      });
      fetchTasks();
    }
  };

  const statusBadge = (b) => {
    const status = (b.status || '').toLowerCase();
    const cls =
      status === 'rejected'
        ? 'bg-danger'
        : status === 'needs'
        ? 'bg-secondary'
        : status === 'in_progress'
        ? 'bg-warning text-dark'
        : 'bg-success';
    const label =
      status === 'rejected'
        ? t('tasksDashboard.statusRejected')
        : status === 'needs'
        ? `⚪ ${t('tasksDashboard.statusNeeds')}`
        : status === 'in_progress'
        ? `🟢 ${t('tasksDashboard.statusInProgress')}`
        : `✅ ${t('tasksDashboard.statusDone')}`;
    return <span className={`badge me-1 ${cls}`}>{label}</span>;
  };

  const priorityChipClass = (tier) => {
    switch (tier) {
      case 'low':
        return 'tasks-chip--priority-low';
      case 'medium':
        return 'tasks-chip--priority-medium';
      case 'high':
        return 'tasks-chip--priority-high';
      case 'urgent':
        return 'tasks-chip--priority-urgent';
      default:
        return 'tasks-chip--priority-medium';
    }
  };

  const renderTaskCard = (b) => {
    const tier = priorityToTier(b.priority);
    const overdue = isOverdue(b);
    const ctxDisplay = buildTaskContextDisplay(b, entityMaps, t);
    const typeKey = b.taskType || 'other';
    const sys = (b.source || 'manual') === 'system';
    const canCompleteQuickly = canActOnTask(b);
    const completing = Boolean(completingTaskIds[b._id]);

    return (
      <article
        key={b._id}
        className={`tasks-task-card${overdue ? ' tasks-task-card--overdue' : ''}`}
      >
        <h3 className="tasks-task-card__title">{getTaskDisplayTitle(b)}</h3>
        <div className="tasks-task-card__meta">
          {ctxDisplay ? (
            <span className="tasks-chip tasks-chip--location">{ctxDisplay}</span>
          ) : (
            <span className="tasks-chip">—</span>
          )}
          <span className="tasks-chip tasks-chip--type">{taskTypeLabel(typeKey)}</span>
          <span className={`tasks-chip ${priorityChipClass(tier)}`}>
            {priorityTierLabel(tier)}
          </span>
          {sys ? (
            <span className="tasks-chip tasks-chip--system">{t('tasksDashboard.systemTask')}</span>
          ) : null}
        </div>
        <div
          className={`tasks-task-card__due${overdue ? ' tasks-task-card__due--warn' : ''}`}
        >
          {b.dueAt
            ? `${t('tasksDashboard.dueLabel')}: ${formatDate(b.dueAt)}`
            : t('tasksDashboard.noDueDate')}
          {overdue ? ` · ${t('tasksDashboard.summaryOverdue')}` : ''}
        </div>
        <TaskCardNotes task={b} t={t} formatDate={formatDate} />
        <div className="tasks-task-card__status">{statusBadge(b)}</div>
        <div className="tasks-task-card__actions">
          {canCompleteQuickly ? (
            <>
              <button
                type="button"
                className="btn btn-ios btn-ios--primary btn-sm tasks-task-card__complete"
                onClick={() => closeTask(b._id)}
                disabled={completing}
              >
                {completing ? (
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                ) : (
                  <i className="fas fa-check" aria-hidden="true" />
                )}
                <span>{t('tasksDashboard.completeTask')}</span>
              </button>
              <button
                type="button"
                className="btn btn-ios btn-ios--muted btn-sm"
                onClick={() => openPostponeModal(b)}
              >
                {t('tasksDashboard.postpone')}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="btn btn-ios btn-ios--muted btn-sm"
            onClick={() => openDetail(b)}
          >
            {t('tasksDashboard.openDetails')}
          </button>
        </div>
      </article>
    );
  };

  const systemOpenTasks = openList.filter((b) => (b.source || 'manual') === 'system');

  const detail = detailTask;
  const detailTier = detail ? priorityToTier(detail.priority) : 'medium';

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <div className="tasks-page admin-page" dir={dir}>
        <header className="tasks-page__header">
          <h1>{t('tasksDashboard.pageTitle')}</h1>
          <p className="tasks-page__subtitle">{t('tasksDashboard.subtitle')}</p>
        </header>

        <div className="tasks-page__summary">
          <div className="tasks-summary-card tasks-summary-card--urgent">
            <span className="tasks-summary-card__label">{t('tasksDashboard.summaryUrgent')}</span>
            <span className="tasks-summary-card__value">{summary.urgent}</span>
          </div>
          <div className="tasks-summary-card tasks-summary-card--today">
            <span className="tasks-summary-card__label">{t('tasksDashboard.summaryToday')}</span>
            <span className="tasks-summary-card__value">{summary.today}</span>
          </div>
          <div className="tasks-summary-card tasks-summary-card--overdue">
            <span className="tasks-summary-card__label">{t('tasksDashboard.summaryOverdue')}</span>
            <span className="tasks-summary-card__value">{summary.overdue}</span>
          </div>
          <div className="tasks-summary-card tasks-summary-card--system">
            <span className="tasks-summary-card__label">{t('tasksDashboard.summarySystem')}</span>
            <span className="tasks-summary-card__value">{summary.system}</span>
          </div>
          <div className="tasks-summary-card tasks-summary-card--done">
            <span className="tasks-summary-card__label">{t('tasksDashboard.summaryCompleted')}</span>
            <span className="tasks-summary-card__value">{summary.completed}</span>
          </div>
        </div>

        <div className="tasks-filters">
          <div className="tasks-filters__row">
            <div className="tasks-filters__field">
              <label className="tasks-filters__label" htmlFor="tasks-filter-room">
                {t('tasksDashboard.filterRoom')}
              </label>
              <select
                id="tasks-filter-room"
                className="form-select form-select-sm tasks-filters__control"
                value={filterRoom}
                onChange={(e) => setFilterRoom(e.target.value)}
              >
                <option value="">{t('tasksDashboard.filterAllRooms')}</option>
                {entityRooms.map((r) => {
                  const id = String(r._id || r.id || '');
                  return (
                    <option key={id} value={id}>
                      {r.name || id.slice(-6)}
                    </option>
                  );
                })}
                <option value={UNASSIGNED_ROOM_GROUP_KEY}>{t('tasksDashboard.groupNoRoom')}</option>
              </select>
            </div>
            <div className="tasks-filters__field">
              <label className="tasks-filters__label" htmlFor="tasks-filter-type">
                {t('tasksDashboard.filterTaskType')}
              </label>
              <select
                id="tasks-filter-type"
                className="form-select form-select-sm tasks-filters__control"
                value={filterTaskType}
                onChange={(e) => setFilterTaskType(e.target.value)}
              >
                <option value="">{t('tasksDashboard.filterAllTypes')}</option>
                {TASK_TYPE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {taskTypeLabel(k)}
                  </option>
                ))}
              </select>
            </div>
            <div className="tasks-filters__field">
              <label className="tasks-filters__label" htmlFor="tasks-filter-priority">
                {t('tasksDashboard.filterPriority')}
              </label>
              <select
                id="tasks-filter-priority"
                className="form-select form-select-sm tasks-filters__control"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <option value="">{t('tasksDashboard.filterAllPriorities')}</option>
                <option value="low">{t('tasksDashboard.priorityLow')}</option>
                <option value="medium">{t('tasksDashboard.priorityMedium')}</option>
                <option value="high">{t('tasksDashboard.priorityHigh')}</option>
                <option value="urgent">{t('tasksDashboard.priorityUrgent')}</option>
              </select>
            </div>
            <div className="tasks-filters__field">
              <label className="tasks-filters__label" htmlFor="tasks-filter-source">
                {t('tasksDashboard.filterSource')}
              </label>
              <select
                id="tasks-filter-source"
                className="form-select form-select-sm tasks-filters__control"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
              >
                <option value="">{t('tasksDashboard.filterAllSources')}</option>
                <option value="manual">{t('tasksDashboard.sourceManual')}</option>
                <option value="system">{t('tasksDashboard.sourceSystem')}</option>
              </select>
            </div>
            <div className="tasks-filters__field tasks-filters__field--search">
              <label className="tasks-filters__label" htmlFor="tasks-filter-search">
                {t('tasksDashboard.filterSearch')}
              </label>
              <input
                id="tasks-filter-search"
                type="search"
                className="form-control form-control-sm tasks-filters__control"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder={t('tasksDashboard.filterSearchPlaceholder')}
                autoComplete="off"
              />
            </div>
            <div className="tasks-filters__field tasks-filters__field--actions">
              <span className="tasks-filters__label tasks-filters__label--spacer" aria-hidden="true">
                {'\u00a0'}
              </span>
              <button
                type="button"
                className="btn btn-ios btn-ios--muted btn-sm tasks-filters__clear"
                disabled={!filtersActive}
                onClick={() => {
                  setFilterRoom('');
                  setFilterTaskType('');
                  setFilterPriority('');
                  setFilterSource('');
                  setFilterSearch('');
                }}
              >
                {t('tasksDashboard.clearFilters')}
              </button>
            </div>
          </div>
        </div>

        <div className="tasks-page__layout">
          <div className="tasks-page__main">
            <div className="tasks-tabs" role="tablist">
              {[
                ['today', t('tasksDashboard.tabToday')],
                ['problems', t('tasksDashboard.tabProblems')],
                ['scheduled', t('tasksDashboard.tabScheduled')],
                ['history', t('tasksDashboard.tabHistory')],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={mainTab === id}
                  className={`tasks-tab${mainTab === id ? ' tasks-tab--active' : ''}`}
                  onClick={() => setMainTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <h2 className="tasks-section-title">{sectionTitle}</h2>

            {mainTab === 'history' ? (
              <div className="tasks-card-grid">
                {historyListFiltered.length === 0 ? (
                  <div className="tasks-empty">{historyListEmptyMessage}</div>
                ) : (
                  historyListFiltered.map((b) => {
                    const tier = priorityToTier(b.priority);
                    const ctxDisplay = buildTaskContextDisplay(b, entityMaps, t);
                    const typeKey = b.taskType || 'other';
                    const sys = (b.source || 'manual') === 'system';
                    return (
                      <article key={b._id} className="tasks-task-card">
                        <h3 className="tasks-task-card__title">{getTaskDisplayTitle(b)}</h3>
                        <div className="tasks-task-card__meta">
                          {ctxDisplay ? (
                            <span className="tasks-chip tasks-chip--location">{ctxDisplay}</span>
                          ) : null}
                          <span className="tasks-chip tasks-chip--type">{taskTypeLabel(typeKey)}</span>
                          <span className={`tasks-chip ${priorityChipClass(tier)}`}>
                            {priorityTierLabel(tier)}
                          </span>
                          {sys ? (
                            <span className="tasks-chip tasks-chip--system">
                              {t('tasksDashboard.systemTask')}
                            </span>
                          ) : null}
                        </div>
                        <TaskCardNotes task={b} t={t} formatDate={formatDate} />
                        <div className="tasks-task-card__due">
                          {b.completedAt
                            ? `${t('tasksDashboard.completedAtLabel')}: ${formatDate(b.completedAt)}`
                            : null}
                        </div>
                        <div className="tasks-task-card__status">{statusBadge(b)}</div>
                        <div className="tasks-task-card__actions">
                          <button
                            type="button"
                            className="btn btn-ios btn-ios--muted btn-sm"
                            onClick={() => openDetail(b)}
                          >
                            {t('tasksDashboard.openDetails')}
                          </button>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            ) : (
              <>
                <div className="tasks-active-area">
                  {activeTasksAfterFilters.length === 0 ? (
                    <div className="tasks-empty">{activeListEmptyMessage}</div>
                  ) : (
                    <div className="tasks-room-groups">
                      {groupedActiveTasks.map((g) => (
                        <section key={g.key} className="tasks-room-group" aria-label={g.title}>
                          <header className="tasks-room-group__header">
                            <h3 className="tasks-room-group__title">{g.title}</h3>
                            <span className="tasks-room-group__count">
                              {t('tasksDashboard.groupTaskCount', g.count)}
                            </span>
                          </header>
                          <div className="tasks-card-grid tasks-card-grid--nested">
                            {g.tasks.map(renderTaskCard)}
                          </div>
                        </section>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-check tasks-history-toggle">
                  <input
                    id="tasks-show-completed"
                    className="form-check-input"
                    type="checkbox"
                    checked={showCompletedMixed}
                    onChange={(e) => setShowCompletedMixed(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="tasks-show-completed">
                    {showCompletedMixed ? t('tasksDashboard.hideCompleted') : t('tasksDashboard.showCompleted')}
                  </label>
                </div>
                {showCompletedMixed ? (
                  <div className="tasks-completed-block">
                    <h3 className="tasks-completed-block__title">
                      {t('tasksDashboard.completedSectionTitle')}
                    </h3>
                    <div className="tasks-card-grid tasks-card-grid--completed">
                      {historyList.length === 0 ? (
                        <div className="tasks-empty tasks-empty--subtle small py-3">
                          {t('tasksDashboard.emptyHistory')}
                        </div>
                      ) : (
                        historyList.map((b) => (
                          <article key={`done-${b._id}`} className="tasks-task-card tasks-task-card--completed">
                            <h3 className="tasks-task-card__title">{getTaskDisplayTitle(b)}</h3>
                            <TaskCardNotes task={b} t={t} formatDate={formatDate} />
                            <div className="tasks-task-card__status">{statusBadge(b)}</div>
                            <div className="tasks-task-card__actions">
                              <button
                                type="button"
                                className="btn btn-ios btn-ios--muted btn-sm"
                                onClick={() => openDetail(b)}
                              >
                                {t('tasksDashboard.openDetails')}
                              </button>
                            </div>
                          </article>
                        ))
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <aside className="tasks-page__sidebar">
            <div className="tasks-panel tasks-quick-add">
              <h3 className="tasks-panel__title">{t('tasksDashboard.quickAddTitle')}</h3>
              <form onSubmit={submit}>
                <div className="mb-2">
                  <label className="form-label" htmlFor="task-quick-desc">
                    {t('tasksDashboard.fieldDescription')}
                  </label>
                  <input
                    id="task-quick-desc"
                    className="form-control"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label" htmlFor="task-quick-loc">
                    {t('tasksDashboard.roomOrLocation')}
                  </label>
                  <input
                    id="task-quick-loc"
                    className="form-control"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>
                {showEntitySelectors ? (
                  <p className="small text-muted mb-2">{t('tasksDashboard.optionalLinksHint')}</p>
                ) : null}
                {entityRooms.length > 0 ? (
                  <div className="mb-2">
                    <label className="form-label" htmlFor="task-quick-room">
                      {t('tasksDashboard.linkRoom')}
                    </label>
                    <select
                      id="task-quick-room"
                      className="form-select"
                      value={refRoomId}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRefRoomId(v);
                        setRefDeviceId((prev) => {
                          if (!prev || !v) return prev;
                          const dev = entityDevices.find((d) => String(d._id || d.id) === prev);
                          if (dev && String(dev.roomId || '') !== v) return '';
                          return prev;
                        });
                      }}
                    >
                      <option value="">{t('tasksDashboard.linkNoneOption')}</option>
                      {entityRooms.map((r) => {
                        const id = String(r._id || r.id || '');
                        return (
                          <option key={id} value={id}>
                            {r.name || id.slice(-6)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : null}
                {entityDevices.length > 0 ? (
                  <div className="mb-2">
                    <label className="form-label" htmlFor="task-quick-device">
                      {t('tasksDashboard.linkDevice')}
                    </label>
                    <select
                      id="task-quick-device"
                      className="form-select"
                      value={refDeviceId}
                      onChange={(e) => setRefDeviceId(e.target.value)}
                    >
                      <option value="">{t('tasksDashboard.linkNoneOption')}</option>
                      {devicesForSelect.map((d) => {
                        const id = String(d._id || d.id || '');
                        const label =
                          (d.displayName || d.name || d.deviceId || '').trim() || id.slice(-6);
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : null}
                {entityStrains.length > 0 ? (
                  <div className="mb-2">
                    <label className="form-label" htmlFor="task-quick-strain">
                      {t('tasksDashboard.linkStrain')}
                    </label>
                    <select
                      id="task-quick-strain"
                      className="form-select"
                      value={refStrainId}
                      onChange={(e) => setRefStrainId(e.target.value)}
                    >
                      <option value="">{t('tasksDashboard.linkNoneOption')}</option>
                      {entityStrains.map((s) => {
                        const id = String(s._id || s.id || '');
                        const label = (s.name || s.slug || '').trim() || id.slice(-6);
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : null}
                {entityCuttings.length > 0 ? (
                  <div className="mb-2">
                    <label className="form-label" htmlFor="task-quick-cutting">
                      {t('tasksDashboard.linkCutting')}
                    </label>
                    <select
                      id="task-quick-cutting"
                      className="form-select"
                      value={refCuttingId}
                      onChange={(e) => setRefCuttingId(e.target.value)}
                    >
                      <option value="">{t('tasksDashboard.linkNoneOption')}</option>
                      {entityCuttings.map((c) => {
                        const id = String(c._id || c.id || '');
                        const label = [c.code, c.shortId].filter(Boolean).join(' · ') || id.slice(-6);
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : null}
                <div className="mb-2">
                  <label className="form-label" htmlFor="task-quick-type">
                    {t('tasksDashboard.taskType')}
                  </label>
                  <select
                    id="task-quick-type"
                    className="form-select"
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                  >
                    {TASK_TYPE_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {taskTypeLabel(k)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label" htmlFor="task-quick-priority">
                    {t('tasksDashboard.fieldPriority')}
                  </label>
                  <select
                    id="task-quick-priority"
                    className="form-select"
                    value={priorityTier}
                    onChange={(e) => setPriorityTier(e.target.value)}
                  >
                    <option value="low">{t('tasksDashboard.priorityLow')}</option>
                    <option value="medium">{t('tasksDashboard.priorityMedium')}</option>
                    <option value="high">{t('tasksDashboard.priorityHigh')}</option>
                    <option value="urgent">{t('tasksDashboard.priorityUrgent')}</option>
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label" htmlFor="task-quick-due">
                    {t('tasksDashboard.dueLabel')}
                  </label>
                  <input
                    id="task-quick-due"
                    type="datetime-local"
                    className="form-control"
                    value={dueLocal}
                    onChange={(e) => setDueLocal(e.target.value)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label" htmlFor="task-quick-note">
                    {t('tasksDashboard.fieldComment')}
                  </label>
                  <textarea
                    id="task-quick-note"
                    className="form-control"
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="task-quick-photo">
                    {t('tasksDashboard.addPhotos')}
                  </label>
                  <ImageUploadInput
                    id="task-quick-photo"
                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                    multiple
                    className="form-control tasks-quick-add__upload"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                </div>
                <button className="btn btn-ios btn-ios--primary w-100" type="submit">
                  {t('tasksDashboard.addTask')}
                </button>
              </form>
            </div>

            <div className="tasks-panel">
              <h3 className="tasks-panel__title">{t('tasksDashboard.recommendationsTitle')}</h3>
              {systemOpenTasks.length > 0 ? (
                <>
                  <p className="tasks-panel__hint">{t('tasksDashboard.recommendationsHint')}</p>
                  <ul className="tasks-rec-list">
                    {systemOpenTasks.slice(0, 5).map((task) => (
                      <li key={task._id} className="tasks-rec-item">
                        <span className="tasks-rec-item__badge">{t('tasksDashboard.systemTask')}</span>
                        <div>{getTaskDisplayTitle(task)}</div>
                        {task.location ? (
                          <div className="small text-muted mt-1">{task.location}</div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="tasks-rec-empty">
                  <p className="tasks-rec-empty__primary">
                    {t('tasksDashboard.recommendationsEmptyPrimary')}
                  </p>
                  <p className="tasks-rec-empty__secondary">
                    {t('tasksDashboard.recommendationsEmptySecondary')}
                  </p>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {detail ? (
        <div
          className="modal d-block tasks-modal"
          tabIndex="-1"
          role="dialog"
          onClick={closeDetailModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('tasksDashboard.openDetails')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label={t('cancel')}
                  onClick={closeDetailModal}
                />
              </div>
              <div className="modal-body">
                {editing === detail._id ? (
                  <>
                    <input
                      className="form-control mb-2"
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                    />
                    <button
                      className="btn btn-ios btn-ios--primary btn-sm me-2"
                      type="button"
                      onClick={() => saveEdit(detail._id)}
                    >
                      {t('save')}
                    </button>
                    <button
                      className="btn btn-ios btn-ios--muted btn-sm"
                      type="button"
                      onClick={() => setEditing(null)}
                    >
                      {t('cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="fw-semibold">{getTaskDisplayTitle(detail)}</p>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <span className={`tasks-chip ${priorityChipClass(detailTier)}`}>
                        {priorityTierLabel(detailTier)}
                      </span>
                      {(detail.source || 'manual') === 'system' ? (
                        <span className="tasks-chip tasks-chip--system">
                          {t('tasksDashboard.systemTask')}
                        </span>
                      ) : null}
                      <span className="tasks-chip tasks-chip--type">
                        {taskTypeLabel(detail.taskType || 'other')}
                      </span>
                    </div>
                    {(() => {
                      const ctxLine = buildTaskContextDisplay(detail, entityMaps, t);
                      if (!ctxLine) return null;
                      return <p className="small text-muted mb-1">{ctxLine}</p>;
                    })()}
                    {(detail.source || 'manual') === 'system' &&
                    (String(detail.reason || '').trim() || hasSystemTriggerData(detail)) ? (
                      <div className="tasks-system-reason mb-3">
                        <div className="tasks-system-reason__title small text-uppercase text-muted">
                          {t('tasksDashboard.systemReasonSection')}
                        </div>
                        {String(detail.reason || '').trim() ? (
                          <p className="small mb-2">{String(detail.reason).trim()}</p>
                        ) : null}
                        {hasSystemTriggerData(detail) ? (
                          <div className="small">
                            {detail.triggerType?.trim() ? (
                              <p className="mb-1">
                                <strong>{t('tasksDashboard.systemTriggerTypeLabel')}:</strong>{' '}
                                {detail.triggerType.trim()}
                              </p>
                            ) : null}
                            {detail.triggerMetric?.trim() ? (
                              <p className="mb-1">
                                <strong>{t('tasksDashboard.systemTriggerMetricLabel')}:</strong>{' '}
                                {detail.triggerMetric.trim()}
                              </p>
                            ) : null}
                            {formatSystemValueWithUnit(detail) ? (
                              <p className="mb-1">
                                <strong>{t('tasksDashboard.systemTriggerValueLabel')}:</strong>{' '}
                                {formatSystemValueWithUnit(detail)}
                              </p>
                            ) : null}
                            {formatSystemAllowedRangeText(detail, t) ? (
                              <p className="mb-1">
                                <strong>{t('tasksDashboard.systemAllowedRangeLabel')}:</strong>{' '}
                                {formatSystemAllowedRangeText(detail, t)}
                              </p>
                            ) : null}
                            {detail.triggeredAt &&
                            !Number.isNaN(new Date(detail.triggeredAt).getTime()) ? (
                              <p className="mb-0">
                                <strong>{t('tasksDashboard.systemTriggeredAt')}:</strong>{' '}
                                {formatDate(new Date(detail.triggeredAt))}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {detail.dueAt ? (
                      <p className="small text-muted mb-1">
                        {t('tasksDashboard.dueLabel')}: {formatDate(detail.dueAt)}
                      </p>
                    ) : null}
                    <div className="mb-2">{statusBadge(detail)}</div>
                    {canActOnTask(detail) && (
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {editing !== detail._id && (
                          <button
                            type="button"
                            className="btn btn-ios btn-ios--muted btn-sm"
                            onClick={() => {
                              setEditing(detail._id);
                              setEditDesc(detail.description);
                            }}
                          >
                            {t('update')}
                          </button>
                        )}
                        {(detail.status || '').toLowerCase() !== 'done' && (
                          <button
                            type="button"
                            className="btn btn-ios btn-ios--primary btn-sm"
                            onClick={() => closeTask(detail._id)}
                          >
                            {t('tasksDashboard.completeTask')}
                          </button>
                        )}
                        {!isWorkerRole(normalizedRole) ? (
                          <button
                            type="button"
                            className="btn btn-ios btn-ios--destructive btn-sm"
                            onClick={() => deleteTask(detail._id)}
                          >
                            {t('delete')}
                          </button>
                        ) : null}
                      </div>
                    )}
                    {canManagePriority(detail) && (detail.status || '').toLowerCase() !== 'done' ? (
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="small text-muted">{t('tasksDashboard.fieldPriority')}</span>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={prioritySaving[detail._id] || (detail.priority ?? 3) >= 5}
                            onClick={() => changePriority(detail, 1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={prioritySaving[detail._id] || (detail.priority ?? 3) <= 1}
                            onClick={() => changePriority(detail, -1)}
                          >
                            ▼
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {['admin', 'superuser'].includes(normalizedRole) && (
                      <div className="mb-3">
                        <label className="form-label small">{t('status')}</label>
                        <select
                          className="form-select form-select-sm"
                          value={detail.status}
                          onChange={(e) => updateStatus(detail._id, e.target.value)}
                        >
                          <option value="in_progress">{t('tasksDashboard.statusInProgress')}</option>
                          <option value="needs">{t('tasksDashboard.statusNeeds')}</option>
                          <option value="done">{t('tasksDashboard.statusDone')}</option>
                          <option value="rejected">{t('tasksDashboard.statusRejected')}</option>
                        </select>
                      </div>
                    )}
                  </>
                )}
                {Array.isArray(detail.comments) && detail.comments.length > 0 && (
                  <div className="mb-2 mt-2">
                    <div className="small text-uppercase text-muted mb-1">{t('tasksDashboard.fieldComment')}</div>
                    {detail.comments
                      .slice()
                      .reverse()
                      .map((c, idx) => (
                        <div key={idx} className="border-top pt-2">
                          <small className="text-muted d-block">
                            {formatDate(c.createdAt)} —{' '}
                            {c.createdBy?.displayName || c.createdBy?.login || 'Unknown'}
                          </small>
                          <p className="mb-1">{c.text}</p>
                        </div>
                      ))}
                  </div>
                )}
                {detail.photos && detail.photos.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {detail.photos.map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        className="btn p-0 border-0 bg-transparent"
                        onClick={() => setViewImg(src)}
                      >
                        <img
                          src={src}
                          alt=""
                          loading="lazy"
                          style={{ maxWidth: '120px', borderRadius: 8 }}
                        />
                      </button>
                    ))}
                  </div>
                )}
                <small className="text-muted d-block">
                  {formatDate(detail.createdAt)}
                </small>
                {detail.completedAt && (
                  <small className="text-muted d-block">
                    {t('tasksDashboard.completedAtLabel')}: {formatDate(detail.completedAt)}
                  </small>
                )}
                <small className="text-muted d-block">
                  {t('openedBy')}: {detail.createdBy?.displayName || detail.createdBy?.login || 'Unknown'}
                </small>
                <small className="text-muted d-block">
                  {t('tasksDashboard.lastActivity')}: {formatDate(detail.lastActivityAt || detail.createdAt)}
                </small>
                {(detail.status || '').toLowerCase() !== 'done' ? (
                  <div className="input-group input-group-sm mt-3">
                    <input
                      className="form-control"
                      placeholder={t('tasksDashboard.fieldComment')}
                      value={newComments[detail._id] || ''}
                      onChange={(e) =>
                        setNewComments({ ...newComments, [detail._id]: e.target.value })
                      }
                    />
                    <button
                      className="btn btn-ios btn-ios--primary"
                      type="button"
                      onClick={() => addComment(detail._id)}
                    >
                      {t('tasksDashboard.addComment')}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {postponeTarget ? (
        <div
          className="modal d-block tasks-modal tasks-modal--postpone"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="postpone-modal-title"
          onClick={closePostponeModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="postpone-modal-title">
                  {t('tasksDashboard.postponeModalTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label={t('cancel')}
                  onClick={closePostponeModal}
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-3">{getTaskDisplayTitle(postponeTarget)}</p>
                <div className="d-grid gap-2 mb-3">
                  <button
                    type="button"
                    className="btn btn-ios btn-ios--muted"
                    onClick={() => applyPostponeDueAt(computeDueLaterToday())}
                  >
                    {t('tasksDashboard.postponeLaterToday')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ios btn-ios--muted"
                    onClick={() => applyPostponeDueAt(computeDueTomorrowMorning())}
                  >
                    {t('tasksDashboard.postponeTomorrowMorning')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ios btn-ios--muted"
                    onClick={() => applyPostponeDueAt(computeDueTomorrowEvening())}
                  >
                    {t('tasksDashboard.postponeTomorrowEvening')}
                  </button>
                </div>
                <label className="form-label" htmlFor="postpone-custom-dt">
                  {t('tasksDashboard.postponeChooseDateTime')}
                </label>
                <input
                  id="postpone-custom-dt"
                  type="datetime-local"
                  className="form-control mb-3 tasks-postpone-datetime"
                  value={postponeCustomLocal}
                  onChange={(e) => setPostponeCustomLocal(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-ios btn-ios--primary w-100"
                  onClick={applyPostponeCustom}
                >
                  {t('tasksDashboard.postponeApply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {viewImg && (
        <div className="modal d-block" tabIndex="-1" role="dialog" onClick={() => setViewImg(null)}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-body p-0" onClick={(e) => e.stopPropagation()}>
                <img src={viewImg} alt="screenshot" loading="lazy" className="img-fluid" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
